/**
 * Completion poller — polls a status function on an interval until it reaches a
 * terminal state or times out. Pure logic, no React dependency (the screen/hook
 * calls these methods).
 *
 * Why this exists: async backend jobs (POC generation, medication extraction)
 * deliver their "done" signal over a best-effort WebSocket push. That push can
 * be missed — a dropped or half-open/"zombie" socket means the event is fanned
 * out to a dead connection and lost, with no replay. When that happens and the
 * RN is sitting on the in-progress screen (no navigation, no reconnect), nothing
 * recovers it. This poller re-reads the server status endpoint on a timer, so
 * the screen converges to the real state regardless of socket liveness. See
 * docs/audits/2026-06-05-poc-stuck-websocket-zombie-rca.md.
 */

export interface CompletionPollerConfig<T> {
    /** Poll interval in ms. The first poll fires one interval after start(). */
    intervalMs: number;
    /** Stop and call onTimeout once this many ms elapse from start(). */
    timeoutMs: number;
    /** Reads current server state. */
    poll: () => Promise<T>;
    /** True when a result is terminal (stop polling). */
    isTerminal: (result: T) => boolean;
    /** Called with every poll result — the caller decides how to react. */
    onResult: (result: T) => void;
    /** Called once if timeoutMs elapses before a terminal result. */
    onTimeout: () => void;
    /** Optional. Observe a failed poll. Default: ignore and keep polling. */
    onError?: (err: unknown) => void;
}

export class CompletionPoller<T> {
    private timer: ReturnType<typeof setTimeout> | null = null;
    private startedAt = 0;
    private stopped = true;
    private config: CompletionPollerConfig<T>;

    constructor(config: CompletionPollerConfig<T>) {
        this.config = config;
    }

    /** Begin polling. No-op if already running. */
    start() {
        if (!this.stopped) {
            return;
        }
        this.stopped = false;
        this.startedAt = Date.now();
        this.scheduleNext();
    }

    /** Stop polling. Safe to call multiple times. */
    stop() {
        this.stopped = true;
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    get isRunning(): boolean {
        return !this.stopped;
    }

    private scheduleNext() {
        if (this.stopped) {
            return;
        }
        this.timer = setTimeout(() => {
            void this.tick();
        }, this.config.intervalMs);
    }

    private async tick() {
        if (this.stopped) {
            return;
        }
        // Time budget exhausted — give up before firing another poll.
        if (Date.now() - this.startedAt >= this.config.timeoutMs) {
            this.stop();
            this.config.onTimeout();
            return;
        }
        // Polls are serialized: the next tick is scheduled only AFTER this
        // poll resolves (below), so two polls never overlap and slow polls
        // can't pile up. poll() itself carries the network timeout (the api
        // request() wrapper), so a hung request rejects rather than wedging.
        try {
            const result = await this.config.poll();
            if (this.stopped) {
                return;
            }
            this.config.onResult(result);
            if (this.config.isTerminal(result)) {
                this.stop();
                return;
            }
        } catch (err) {
            if (!this.stopped) {
                this.config.onError?.(err);
            }
        }
        this.scheduleNext();
    }
}
